import React, { useContext, useState, useRef } from 'react';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { useFormik } from 'formik';
import { postRequest } from '../../../api/ApiController';
import {
  SaveIcon, AddIcon, CopyIcon, VerticalLineIcon, FavoriteIcon, PriceIcon, SaveAndCloseIcon,
} from '../../svg/svgIcons';
import AuthContext from '../../../components/AuthContext';
import UtilContext from '../../../components/UtilContext';
import { LocaleContext } from '../../../components/LocaleContext';
import { ConfirmDialog } from 'primereact/confirmdialog';
import IntersectionObserverWrapper from '../../../components/toolbar/IntersectionObserverWrap';
import { MultiSelect } from 'primereact/multiselect';
import { Calendar } from '../../../components/PrimeCalendar';
import { addLocale } from 'primereact/api';
import { Chips } from 'primereact/chips';
import { Message } from 'primereact/message';
import {
  applySummaryEmailOTKWritePlan,
  buildSummaryEmailOTKWritePayload,
  initialValuesForSummaryEmailOTKForm,
  planSummaryEmailOTKWriteOutcome,
} from '../../../utils/summaryEmailOTKWritePayload';

export default function SummaryEmailOTKAdd({ summary_email, type = 'add' }) {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { openFavoriteDialog, toast, setFastView } = useContext(UtilContext);
  const { t } = useContext(LocaleContext);

  addLocale('sk', {
    firstDayOfWeek: 1,
    showMonthAfterYear: false,
    dayNames: ['Nedela', 'Pondelok', 'Utorok', 'Streda', t('Common.Day.Thursday'), 'Piatok', 'Sobota'],
    dayNamesShort: ['Ne', 'Pon', 'Ut', 'Str', 'Štv', 'Pia', 'So'],
    dayNamesMin: ['Ne', 'Po', 'Ut', 'Str', 'Štv', 'Pi', 'So'],
    monthNames: [t('Common.Month.January'), t('Common.Month.February'), 'Marec', t('Common.Month.April'), 'Máj ', t('Common.Month.June'), t('Common.Month.July'), 'August', 'September', t('Common.Month.October'), 'November', 'December'],
    monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'Máj ', t('Common.Month.June'), t('Common.Month.July'), 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'],
    today: 'Dnes',
    clear: t('Common.Delete'),
  });

  const closeAfterRef = useRef(false);
  const [throwInvalidEmail, setThrowInvalidEmail] = useState(false);
  const submitRef = useRef(null);
  const [formSummaryEmail, setFormSummaryEmail] = useState(summary_email);
  const [formType, setFormType] = useState(type);
  const [insertCopyIcon] = useState(!user.permissions?.includes('add otk-setup'));

  function addNewSummaryEmail() {
    dispatch({
      type: 'create_new_tab',
      name: t('SummaryEmailOTKAdd.Tab.Title') || t('Common.SummaryEmail'), translationKey: 'SummaryEmailOTKAdd.Tab.Title', legacyLabel: t('SummaryEmailOTKAdd.Tab.Title') || t('Common.SummaryEmail'),
      page: 'SummaryEmailOTKAdd',
      props: { summary_email: null, type: 'add' },
      mask: 'qlty025',
    });
  }

  function copySummaryEmail() {
    dispatch({
      type: 'create_new_tab',
      name: t('SummaryEmailOTKAdd.Tab.Title') || t('Common.SummaryEmail'), translationKey: 'SummaryEmailOTKAdd.Tab.Title', legacyLabel: t('SummaryEmailOTKAdd.Tab.Title') || t('Common.SummaryEmail'),
      page: 'SummaryEmailOTKAdd',
      props: { summary_email: formSummaryEmail, type: 'add' },
      mask: 'qlty025',
    });
  }

  const formik = useFormik({
    initialValues: initialValuesForSummaryEmailOTKForm(formSummaryEmail, formType),
    enableReinitialize: true,
    validate: () => ({}),
    onSubmit: async (values) => {
      const shouldClose = !!closeAfterRef.current;
      closeAfterRef.current = false;
      const payload = buildSummaryEmailOTKWritePayload(values);
      try {
        const saveRes =
          formType === 'add'
            ? await postRequest('/api/summary_emails', payload, true)
            : await postRequest(`/api/summary_emails/update/${values.id}`, payload, true);

        const plan = planSummaryEmailOTKWriteOutcome({
          response: saveRes,
          closeAfter: shouldClose,
        });

        if (plan.outcome === 'error' || plan.outcome === 'unknown') {
          alert(t('SummaryEmailOTKAdd.Error.SaveFailed') || t('Common.Toast.ActionFailedDetail'));
          return;
        }

        if (plan.canonicalValues) {
          formik.setValues(plan.canonicalValues);
          if (saveRes?.data) setFormSummaryEmail(saveRes.data);
        } else {
          formik.setValues(values);
        }
        if (plan.outcome === 'created') setFormType('edit');

        applySummaryEmailOTKWritePlan(plan, {
          toast,
          t,
          dispatch,
          selectedTabId: state.selectedTab,
        });
      } catch (_reason) {
        if (!navigator.onLine) {
          alert(t('SummaryEmailOTKAdd.Error.Offline') || t('ServiceTask.OfflineQueuedAlert'));
        } else {
          alert(t('SummaryEmailOTKAdd.Error.SaveFailed') || t('Common.Toast.ActionFailedDetail'));
        }
      }
    },
  });

  // Business option values are stored as SK domain data — not UI chrome.
  const submodulOptions = [
    { name: t('Otk.InternalError') },
    { name: t('Otk.ExternalError') },
    { name: t('Otk.Complaint') },
    { name: t('Otk.Documentation') },
    { name: t('Otk.MeasuringProtocol') },
    { name: 'Sebakontrola' },
    { name: 'NDT protokol' },
    { name: t('Otk.AnnealingProtocol') },
  ];

  const weekTimePeriod = [
    { name: 'Pondelok' },
    { name: 'Utorok' },
    { name: 'Streda' },
    { name: t('Common.Day.Thursday') },
    { name: 'Piatok' },
    { name: 'Sobota' },
    { name: 'Nedeľa' },
  ];

  const monthTimePeriod = Array.from({ length: 31 }, (_, i) => i + 1).map((i) => ({ name: `${i}` }));

  function emailValidation(email) {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (re.test(email)) {
      setThrowInvalidEmail(false);
      return true;
    }
    setThrowInvalidEmail(true);
    return false;
  }

  return (
    <>
      <div className="w-full h-10 flex flex-row bg-[#333]">
        <IntersectionObserverWrapper>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center " data-targetid="saveandclose">
            <button
              className="disabled:opacity-25"
              disabled={insertCopyIcon}
              type="submit"
              title={t('Common.SaveAndClose') || 'Save & Close'}
              onClick={() => {
                closeAfterRef.current = true;
                submitRef.current.click();
              }}
            >
              <SaveAndCloseIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center " data-targetid="save">
            <button
              className="disabled:opacity-25"
              disabled={insertCopyIcon}
              type="submit"
              title={t('Common.Save') || 'Save'}
              onClick={() => {
                closeAfterRef.current = false;
                submitRef.current.click();
              }}
            >
              <SaveIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center " data-targetid="add">
            <button className="disabled:opacity-25" disabled={insertCopyIcon} onClick={() => addNewSummaryEmail()}>
              <AddIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center " data-targetid="copy">
            <button className="disabled:opacity-25" disabled={insertCopyIcon} onClick={() => copySummaryEmail()}>
              <CopyIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center " data-targetid="vert">
            <VerticalLineIcon />
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center " data-targetid="favorite">
            <button
              onClick={() =>
                openFavoriteDialog(t('SummaryEmailOTKAdd.FavoriteTitle') || t('Common.NewSummaryEmail'), 'SummaryEmailAdd')
              }
            >
              <FavoriteIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center " data-targetid="fast">
            <button onClick={() => setFastView('SummaryEmailAdd', null, null)}>
              <PriceIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center " data-targetid="vert">
            <VerticalLineIcon />
          </div>
        </IntersectionObserverWrapper>
      </div>
      <ConfirmDialog />
      <div className="overflow-y-auto h-[calc(100vh-177px)]">
        <form onSubmit={formik.handleSubmit}>
          <div className="relative flex flex-col justify-center overflow-x">
            <div className="xl:grid grid-cols-2 gap-1 mb-2 ">
              <div className="w-auto p-6 m-4 bg-gray-100 rounded-md shadow-md">
                <h1 className="text-3xl font-semibold text-left text-zinc-900">
                  {t('SummaryEmailOTKAdd.Heading') || t('Common.SummaryEmail')}
                </h1>
                {formType === 'edit' && (
                  <label className="block text-md font-semibold text-gray-800">
                    {(t('SummaryEmailOTKAdd.Label.Id') || 'Por.č.:')} {formik.values.id}
                  </label>
                )}
                <div>
                  <label htmlFor="name" className="block text-md font-semibold text-gray-800">
                    {t('SummaryEmailOTKAdd.Label.Name') || t('Common.Name')}
                  </label>
                  <input
                    name="name"
                    type="text"
                    required
                    onChange={formik.handleChange}
                    value={formik.values.name ?? ''}
                    className="block w-full px-4 py-2 mt-2 mb-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                  />
                </div>
                <div>
                  <label htmlFor="data" className="block text-md font-semibold text-gray-800">
                    {t('SummaryEmailOTKAdd.Label.Submodules') || 'Podmoduly'}
                  </label>
                  <MultiSelect
                    value={formik.values.data ?? []}
                    onChange={(e) => formik.setFieldValue('data', e.value ?? [])}
                    options={submodulOptions}
                    optionLabel="name"
                    filter
                    placeholder={t('SummaryEmailOTKAdd.Placeholder.Submodules') || t('Otk.SelectSubmodules')}
                    className="w-full px-4 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                  />
                </div>
                <div>
                  <label htmlFor="status" className="block text-md font-semibold text-gray-800">
                    {t('SummaryEmailOTKAdd.Label.Type') || t('Common.Type')}
                  </label>
                  <select
                    name="status"
                    required
                    onChange={formik.handleChange}
                    value={formik.values.status ?? ''}
                    className="bg-white block w-full  px-4 py-2 mt-2 text-grey-700 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                  >
                    <option value="">{t('SummaryEmailOTKAdd.Placeholder.Select') || t('Common.SelectOption')}</option>
                    <option value="Všetky">{t('Common.All')}</option>
                    <option value="Otvorené">{t('Status.OpenPlural')}</option>
                    <option value="Uzavreté">{t('PriorityList.Filter.Closed')}</option>
                    <option value="Obchodní partneri">{t('BusinessPartnerList')}</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="interval" className="block text-md font-semibold text-gray-800">
                    {t('SummaryEmailOTKAdd.Label.Interval') || 'Interval'}
                  </label>
                  <select
                    name="interval"
                    required
                    onChange={formik.handleChange}
                    value={formik.values.interval ?? ''}
                    className="bg-white block w-full  px-4 py-2 mt-2 text-grey-700 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                  >
                    <option value="">{t('SummaryEmailOTKAdd.Placeholder.Select') || t('Common.SelectOption')}</option>
                    <option value="Všetky">{t('Common.FilterAll')}</option>
                    {formik.values.status !== 'Obchodní partneri' && <option value="Predch. deň">{t('Common.PrevDay')}</option>}
                    {formik.values.status !== 'Obchodní partneri' && (
                      <option value="Predch. týždeň">{t('Common.PrevWeek')}</option>
                    )}
                    {formik.values.status !== 'Obchodní partneri' && (
                      <option value="Predch. mesiac">Predch. mesiac</option>
                    )}
                    {formik.values.status === 'Obchodní partneri' && (
                      <option value="30 dní do exp.">{t('Common.DaysToExp30')}</option>
                    )}
                    {formik.values.status === 'Obchodní partneri' && (
                      <option value="15 dní do exp.">{t('Common.DaysToExp15')}</option>
                    )}
                    {formik.values.status === 'Obchodní partneri' && <option value="Po expirácii">{t('Common.AfterExpiration')}</option>}
                    {formik.values.status !== 'Obchodní partneri' && <option value="Vlastný">{t('Ui.R67.Vlastny')}</option>}
                  </select>
                </div>
                {formik.values.interval === 'Vlastný' && (
                  <div>
                    <label htmlFor="dates" className="block text-md font-semibold text-gray-800">
                      {t('SummaryEmailOTKAdd.Label.DateRange') || 'OD - DO:'}
                    </label>
                    <Calendar
                      required
                      id="buttondisplay"
                      name="dates"
                      style={{
                        border: '1px solid #e5e7eb',
                        transition: 'background-color 0.2s, color 0.2s, border-color 0.2s, box-shadow 0.2s',
                        borderRadius: '6px',
                      }}
                      value={formik.values.dates ?? ''}
                      onChange={(e) => formik.setFieldValue('dates', e.target.value ?? '')}
                      showIcon
                      className="w-full"
                      locale="sk"
                      dateFormat="dd.mm.yy"
                      selectionMode="range"
                      hideOnRangeSelection
                    />
                  </div>
                )}
                <div>
                  <label htmlFor="regularity" className="block text-md font-semibold text-gray-800">
                    {t('SummaryEmailOTKAdd.Label.Schedule') || t('Otk.Schedule')}
                  </label>
                  <select
                    name="regularity"
                    required
                    onChange={(e) => {
                      formik.setFieldValue('regularity', e.target.value);
                      formik.setFieldValue('time_period', []);
                    }}
                    value={formik.values.regularity ?? ''}
                    className="bg-white block w-full  px-4 py-2 mt-2 text-grey-700 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                  >
                    <option value="">{t('SummaryEmailOTKAdd.Placeholder.Select') || t('Common.SelectOption')}</option>
                    <option value="Denne">Denne</option>
                    <option value="Týždenne">{t('Common.Weekly')}</option>
                    <option value="Mesačne">{t('Common.Monthly')}</option>
                  </select>
                </div>
                {formik.values.regularity === 'Mesačne' && (
                  <div>
                    <label htmlFor="time_period" className="block text-md font-semibold text-gray-800">
                      {t('SummaryEmailOTKAdd.Label.Regularity') || t('Common.Frequency')}
                    </label>
                    <MultiSelect
                      required
                      value={formik.values.time_period ?? []}
                      onChange={(e) => formik.setFieldValue('time_period', e.value ?? [])}
                      options={monthTimePeriod}
                      optionLabel="name"
                      filter
                      placeholder={t('SummaryEmailOTKAdd.Placeholder.Regularity') || t('Common.SelectFrequency')}
                      className="w-full px-4 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                    />
                  </div>
                )}
                {formik.values.regularity === 'Týždenne' && (
                  <div>
                    <label htmlFor="time_period" className="block text-md font-semibold text-gray-800">
                      {t('SummaryEmailOTKAdd.Label.Regularity') || t('Common.Frequency')}
                    </label>
                    <MultiSelect
                      required
                      value={formik.values.time_period ?? []}
                      onChange={(e) => formik.setFieldValue('time_period', e.value ?? [])}
                      options={weekTimePeriod}
                      optionLabel="name"
                      filter
                      placeholder={t('SummaryEmailOTKAdd.Placeholder.Regularity') || t('Common.SelectFrequency')}
                      className="w-full px-4 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                    />
                  </div>
                )}
                <div>
                  <label htmlFor="email_time" className="block text-md font-semibold text-gray-800">
                    {t('SummaryEmailOTKAdd.Label.EmailTime') || t('Otk.EmailSendTimeUpper')}
                  </label>
                  <input
                    name="email_time"
                    type="time"
                    min="05:00"
                    max="22:00"
                    required
                    onChange={formik.handleChange}
                    value={formik.values.email_time ?? ''}
                    className="block w-full px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                  />
                </div>
                <div>
                  <label htmlFor="employees" className="block text-md font-semibold text-gray-800">
                    {t('SummaryEmailOTKAdd.Label.Employees') || 'ZAMESTNANCI'}
                  </label>
                  <MultiSelect
                    value={formik.values.employees ?? []}
                    onChange={(e) => formik.setFieldValue('employees', e.value ?? [])}
                    options={(state.employees || [])
                      .filter((employee) => employee.email != null && employee.email !== '')
                      .map((employee) => ({ name: employee.name, id: employee.id }))}
                    optionLabel="name"
                    filter
                    placeholder={t('SummaryEmailOTKAdd.Placeholder.Employees') || t('Common.SelectUsers')}
                    className="w-full px-4 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                  />
                </div>
                <div>
                  <label htmlFor="other_emails" className="block text-md font-semibold text-gray-800">
                    {t('SummaryEmailOTKAdd.Label.OtherEmails') || t('Otk.OtherEmailsUpper')}
                  </label>
                  <Chips
                    value={formik.values.other_emails ?? []}
                    onChange={(e) => formik.setFieldValue('other_emails', e.value ?? [])}
                    onAdd={(e) => emailValidation(e.value)}
                    separator=","
                    className="block w-full px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                  />
                  {throwInvalidEmail && (
                    <Message
                      severity="error"
                      text={t('SummaryEmailOTKAdd.Error.InvalidEmail') || t('Common.InvalidEmail')}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          <button type="submit" ref={submitRef} className="hidden" />
        </form>
      </div>
    </>
  );
}
